import { DocumentListStoreRegistry } from '@packages/shared/document-list/registry';
import type { ComposeStoreBase } from '@packages/shared/compose-store/types';
import { getFormSideStores } from './formSideStoreRegistry';

export const COMPOSE_CREATE_SESSION_KEY = '__new__';

export function deriveFormSessionKey(
  mode: string,
  params?: Record<string, string>,
): string {
  const id = params?.id?.trim();
  if (mode === 'create' || !id) return COMPOSE_CREATE_SESSION_KEY;
  return id;
}

export function resolveFormSchemaId(
  meta: Record<string, unknown> | undefined,
  namespace: string | undefined,
): string | undefined {
  const fromMeta = meta?.formSchemaId;
  if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  if (namespace) return `wealthlane://forms/${namespace}`;
  return undefined;
}

type SessionCapableListStore = ComposeStoreBase<unknown[]> & {
  set(key: string, items: unknown[]): void;
};

function asListStore(store: unknown): SessionCapableListStore | null {
  if (store == null || typeof store !== 'object') return null;
  const candidate = store as SessionCapableListStore;
  if (
    typeof candidate.setActiveSession !== 'function' ||
    typeof candidate.set !== 'function' ||
    typeof candidate.clearActiveSession !== 'function'
  ) {
    return null;
  }
  return candidate;
}

function bindSideStore(
  store: ComposeStoreBase<unknown>,
  formSessionKey: string,
  mode: string,
): void {
  if (mode === 'create') {
    store.clear(COMPOSE_CREATE_SESSION_KEY);
    store.setActiveSession(COMPOSE_CREATE_SESSION_KEY);
    return;
  }
  store.setActiveSession(formSessionKey);
}

function bindDocumentListStore(registryKey: string, formSessionKey: string, mode: string): void {
  const store = DocumentListStoreRegistry.get(registryKey);
  const listStore = asListStore(store);
  if (!listStore) return;

  if (mode === 'create') {
    // Re-bind only — never reset the shared __new__ bucket here. Parent + nested add-document
    // modals all accumulate into the same key until the parent form saves or dismisses.
    listStore.setActiveSession(COMPOSE_CREATE_SESSION_KEY);
    return;
  }
  listStore.setActiveSession(formSessionKey);
}

export interface BeginComposeFormSessionArgs {
  formSessionKey: string;
  mode: string;
  documentListStoreKeys: string[];
  formSchemaId?: string;
}

export function beginComposeFormSession(args: BeginComposeFormSessionArgs): void {
  const { formSessionKey, mode, documentListStoreKeys, formSchemaId } = args;

  for (const registryKey of documentListStoreKeys) {
    bindDocumentListStore(registryKey, formSessionKey, mode);
  }

  const sideStores = [
    ...getFormSideStores(formSchemaId),
  ];
  for (const store of sideStores) {
    bindSideStore(store, formSessionKey, mode);
  }
}

export interface EndComposeFormSessionArgs {
  formSessionKey: string;
  mode: string;
  documentListStoreKeys: string[];
  formSchemaId?: string;
  reason: 'exit' | 'success';
}

function clearActiveOnDocumentStores(keys: string[]): void {
  for (const registryKey of keys) {
    const store = DocumentListStoreRegistry.get(registryKey);
    const listStore = asListStore(store);
    listStore?.clearActiveSession();
  }
}

function clearActiveOnSideStores(stores: ComposeStoreBase<unknown>[]): void {
  for (const store of stores) {
    store.clearActiveSession();
  }
}

/** Close without save = cancel — drop in-memory drafts for this entity bucket. */
function clearSessionBucketOnDismiss(
  formSessionKey: string,
  mode: string,
  documentListStoreKeys: string[],
  sideStores: ComposeStoreBase<unknown>[],
): void {
  if (mode === 'create') {
    for (const registryKey of documentListStoreKeys) {
      const store = DocumentListStoreRegistry.get(registryKey);
      asListStore(store)?.clear(COMPOSE_CREATE_SESSION_KEY);
    }
    for (const store of sideStores) {
      store.clear(COMPOSE_CREATE_SESSION_KEY);
    }
    return;
  }

  if (formSessionKey === COMPOSE_CREATE_SESSION_KEY) return;

  for (const registryKey of documentListStoreKeys) {
    const store = DocumentListStoreRegistry.get(registryKey);
    asListStore(store)?.clear(formSessionKey);
  }
  for (const store of sideStores) {
    store.clear(formSessionKey);
  }
}

function clearAllDocumentStoreBuckets(formSessionKey: string): void {
  if (formSessionKey === COMPOSE_CREATE_SESSION_KEY) return;
  DocumentListStoreRegistry.forEach((_key, store) => {
    asListStore(store)?.clear(formSessionKey);
  });
}

export function endComposeFormSession(args: EndComposeFormSessionArgs): void {
  const { formSessionKey, mode, documentListStoreKeys, formSchemaId, reason } = args;

  const sideStores = getFormSideStores(formSchemaId);

  if (reason === 'exit') {
    clearActiveOnDocumentStores(documentListStoreKeys);
    clearActiveOnSideStores(sideStores);
    clearSessionBucketOnDismiss(formSessionKey, mode, documentListStoreKeys, sideStores);
    return;
  }

  void formSessionKey;
  clearActiveOnDocumentStores(documentListStoreKeys);
  clearActiveOnSideStores(sideStores);
}

/**
 * Modal exit cleanup — detach active session pointers only.
 *
 * Nested add-document modals (grant agreement / notice / T&C) close with `mode: 'create'` and
 * usually no `formSchemaId`. They must NOT wipe the shared `GrantDocumentStore` bucket still
 * owned by the open parent plan/grant form. Bucket deletion happens in `endComposeFormSession`
 * when the parent form block unmounts (cancel) or after successful save in domain actions.
 */
export function dismissComposeFormSideStores(args: {
  formSessionKey: string;
  mode: string;
  formSchemaId?: string;
}): void {
  const sideStores = args.formSchemaId ? getFormSideStores(args.formSchemaId) : [];

  clearActiveOnSideStores(sideStores);

  DocumentListStoreRegistry.forEach((_key, store) => {
    const listStore = asListStore(store);
    listStore?.clearActiveSession();
  });

  if (!args.formSchemaId) {
    return;
  }

  if (args.mode === 'create') {
    DocumentListStoreRegistry.forEach((_key, store) => {
      asListStore(store)?.clear(COMPOSE_CREATE_SESSION_KEY);
    });
    for (const store of sideStores) {
      store.clear(COMPOSE_CREATE_SESSION_KEY);
    }
    return;
  }

  if (args.formSessionKey !== COMPOSE_CREATE_SESSION_KEY) {
    clearAllDocumentStoreBuckets(args.formSessionKey);
    for (const store of sideStores) {
      store.clear(args.formSessionKey);
    }
  }
}
